import { BrowserRouter, Routes, Route, Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { AppShellLayout } from './components/AppShellLayout';
import { ProvinceProvider } from './contexts/ProvinceContext';
import { ToastProvider } from './components/ui/Toast';
import { COLORS } from './constants/theme';
import { LandingPage } from './pages/LandingPage';
import { CreateProvince } from './pages/CreateProvince';
import { ThroneRoom } from './pages/ThroneRoom';
import { RealmHub } from './pages/RealmHub';
import { WarHub } from './pages/WarHub';
import { KingdomHub } from './pages/KingdomHub';
import { WorldHub } from './pages/WorldHub';
import { Buildings } from './pages/Buildings';
import { Training } from './pages/Training';
import { Explore } from './pages/Explore';
import { Science } from './pages/Science';
import { Economy } from './pages/Economy';
import { Magic } from './pages/Magic';
import { Kingdom } from './pages/Kingdom';
import { CouncilRedirect } from './pages/CouncilRedirect';
import { Thievery } from './pages/Thievery';
import { Aid } from './pages/Aid';
import { Attack } from './pages/Attack';
import { Intel } from './pages/Intel';
import { TargetFinder } from './pages/TargetFinder';
import { Strategy } from './pages/Strategy';
import { Dossier } from './pages/Dossier';
import { KingdomViewer } from './pages/KingdomViewer';
import { Politics } from './pages/Politics';
import { Dragons } from './pages/Dragons';
import { RecentNews } from './pages/RecentNews';
import { ProvinceEventFeed } from './pages/ProvinceEventFeed';
import { Relations } from './pages/Relations';
import { Rankings } from './pages/Rankings';
import { Profile } from './pages/Profile';
import { VerifyEmail } from './pages/VerifyEmail';
import { ResetPassword } from './pages/ResetPassword';
import { Spinner } from './components/Spinner';
import { ChancelleryShell } from './components/chancellery/ChancelleryShell';
import { RequireAdminAuth } from './components/chancellery/RequireAdminAuth';
import { AdminLogin } from './pages/Chancellery/AdminLogin';
import { UniversesList } from './pages/Chancellery/UniversesList';
import { UniverseDetail } from './pages/Chancellery/UniverseDetail';
import { WorldDetail } from './pages/Chancellery/WorldDetail';

function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.bg }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/province" replace />;
  }

  const autoOpenLogin = searchParams.get('login') === 'true';
  return <LandingPage autoOpenLogin={autoOpenLogin} />;
}

// SF-122 Component 0: one shared `<ProvinceProvider>` ancestor for both the legacy `<Layout>`
// route group and the new `<AppShellLayout>` group, so crossing between the two chrome trees
// doesn't remount province state (and re-flash the loading spinner) — `useProvince()` is consumed
// by `Sidebar`, `ThroneRoom`, and both new shells. `/create-province` (which doesn't call
// `useProvince()`) stays outside this wrapper, matching its current behavior exactly.
// Exported (not just used internally) so the ProvinceRoot sharing test can render it in isolation
// without mounting the entire route tree.
export function ProvinceRoot() {
  return (
    <ProvinceProvider>
      <Outlet />
    </ProvinceProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
        <Routes>
          <Route path="/" element={<PublicRoute />} />
          <Route path="/login" element={<Navigate to="/?login=true" replace />} />
          <Route path="/register" element={<Navigate to="/" replace />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* SF-112: admin-authenticated Chancellery backoffice */}
          <Route path="/chancellery/login" element={<AdminLogin />} />
          <Route element={<RequireAdminAuth />}>
            <Route element={<ChancelleryShell />}>
              <Route path="/chancellery" element={<UniversesList />} />
              <Route path="/chancellery/universes/:universeId" element={<UniverseDetail />} />
              <Route path="/chancellery/universes/:universeId/worlds/:worldId" element={<WorldDetail />} />
            </Route>
          </Route>
          <Route element={<ProtectedRoute />}>
            <Route path="/create-province" element={<CreateProvince />} />
            <Route element={<ProvinceRoot />}>
              {/* SF-122: /province is the sole route migrated to the new mobile-first app shell
                  so far — every other route stays under the legacy <Layout /> until its own
                  follow-up spec migrates it. Both groups coexist for the duration of the rollout. */}
              <Route element={<AppShellLayout />}>
                <Route path="/province" element={<ThroneRoom />} />
                <Route path="/realm" element={<RealmHub />} />
                <Route path="/war" element={<WarHub />} />
                <Route path="/kingdom" element={<KingdomHub />} />
                <Route path="/world" element={<WorldHub />} />
              </Route>
              <Route element={<Layout />}>
                {/* SF-162: Council.tsx deleted outright (its two remaining tabs merged into
                    Training.tsx's Overview tab) — CouncilRedirect preserves the 8 existing
                    hash-based deep links; /council-new repoints straight to Overview since it
                    never carried its own hash historically (Component 5/6). */}
                <Route path="/council" element={<CouncilRedirect />} />
                <Route path="/council-new" element={<Navigate to="/military?tab=overview" replace />} />
                <Route path="/buildings" element={<Buildings />} />
                <Route path="/growth" element={<Navigate to="/buildings" replace />} />
                <Route path="/raze" element={<Navigate to="/buildings?tab=raze" replace />} />
                <Route path="/military" element={<Training />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/sciences" element={<Science />} />
                <Route path="/economy" element={<Economy />} />
                <Route path="/mystics" element={<Magic />} />
                <Route path="/thievery" element={<Thievery />} />
                <Route path="/attack" element={<Attack />} />
                <Route path="/kingdom/aid" element={<Aid />} />
                <Route path="/aid" element={<Navigate to="/kingdom/aid" replace />} />
                <Route path="/intel" element={<Intel />} />
                <Route path="/strategy" element={<Strategy />} />
                <Route path="/world/dossier/:kingdomId?" element={<Dossier />} />
                <Route path="/world/kingdoms/:kingdomId?" element={<KingdomViewer />} />
                <Route path="/world/targets" element={<TargetFinder />} />
                <Route path="/kingdom/politics" element={<Politics />} />
                <Route path="/kingdom/dragons" element={<Dragons />} />
                <Route path="/kingdom/roster" element={<Kingdom />} />
                <Route path="/recent-news" element={<RecentNews />} />
                {/* SF-123: "Incoming Events" -- Throne Room's Recent Activity "Incoming"
                    subsection is the sole entry point; deliberately not registered in
                    WORLD_ITEMS/SIBLING_GROUPS (Decision 12). */}
                <Route path="/province/events" element={<ProvinceEventFeed />} />
                <Route path="/relations" element={<Relations />} />
                <Route path="/rankings" element={<Rankings />} />
                <Route path="/profile" element={<Profile />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/province" replace />} />
        </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
